import Phaser from "phaser";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";

export type TowerTargetPriority = "nearest" | "lowest_hp" | "highest_threat" | "siege_first" | "fast_first";

export type TowerConfig = {
  id: string;
  name: string;
  lane: LaneNumber;
  padId: string;
  x: number;
  y: number;
  texture: string;
  hp: number;
  damage: number;
  range: number;
  attackRateMs: number;
  healing?: boolean;
  blocker?: boolean;
  singleUse?: boolean;
  splashRadius?: number;
  slow?: number;
  scoutCoverage?: boolean;
  deployTimeMs?: number;
  targetPriority?: TowerTargetPriority;
};

export class Tower {
  readonly id: string;
  readonly name: string;
  readonly lane: LaneNumber;
  readonly padId: string;
  damage: number;
  range: number;
  attackRateMs: number;
  readonly healing: boolean;
  readonly blocker: boolean;
  readonly singleUse: boolean;
  readonly splashRadius: number;
  readonly slow: number;
  readonly scoutCoverage: boolean;
  readonly deployTimeMs: number;
  readonly sprite: Phaser.GameObjects.Image;
  targetPriority: TowerTargetPriority;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly hpBack: Phaser.GameObjects.Rectangle;
  private readonly hpFill: Phaser.GameObjects.Rectangle;
  private readonly levelBadge: Phaser.GameObjects.Text;
  private readonly deployRing?: Phaser.GameObjects.Graphics;
  private readonly deploymentStartedAt: number;
  private readonly deploymentReadyAt: number;
  hp: number;
  maxHp: number;
  level = 1;
  nextAttackAt = 0;
  consumed = false;
  destroyed = false;

  constructor(scene: Phaser.Scene, config: TowerConfig) {
    this.id = config.id;
    this.name = config.name;
    this.lane = config.lane;
    this.padId = config.padId;
    this.damage = config.damage;
    this.range = config.range;
    this.attackRateMs = config.attackRateMs;
    this.healing = Boolean(config.healing);
    this.blocker = Boolean(config.blocker);
    this.singleUse = Boolean(config.singleUse);
    this.splashRadius = config.splashRadius ?? 0;
    this.slow = config.slow ?? 0;
    this.scoutCoverage = Boolean(config.scoutCoverage);
    this.deployTimeMs = config.deployTimeMs ?? 0;
    this.targetPriority = config.targetPriority ?? this.defaultTargetPriority(config.id);
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.applyInitialGrowth();
    this.shadow = scene.add.ellipse(config.x, config.y + 18, 54, 16, 0x030305, 0.36).setDepth(50);
    this.sprite = scene.add.image(config.x, config.y, config.texture).setDepth(55).setScale(config.id === "ice_circle" ? 0.58 : 0.62);
    this.applyDisplaySize(scene, config.texture, config.id);
    this.hpBack = scene.add.rectangle(config.x, config.y - 38, 42, 5, 0x08070a, 0.88).setDepth(82).setVisible(false);
    this.hpFill = scene.add.rectangle(config.x - 20, config.y - 38, 40, 3, 0x77d7ff, 0.95).setOrigin(0, 0.5).setDepth(83).setVisible(false);
    this.levelBadge = scene.add
      .text(config.x + 24, config.y - 33, "1", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "10px",
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: 2
      })
      .setOrigin(0.5)
      .setDepth(84)
      .setVisible(false);
    this.deploymentStartedAt = scene.time.now;
    this.deploymentReadyAt = this.deploymentStartedAt + this.deployTimeMs;
    if (this.deployTimeMs > 0) {
      this.nextAttackAt = this.deploymentReadyAt;
      this.deployRing = scene.add.graphics().setDepth(85);
      this.sprite.setAlpha(0.78);
    }
  }

  get x() {
    return this.sprite.x;
  }

  get y() {
    return this.sprite.y;
  }

  canAct(time: number) {
    return !this.destroyed && !this.consumed && time >= this.nextAttackAt && time >= this.deploymentReadyAt;
  }

  update(time: number) {
    if (!this.deployRing) return;
    if (this.destroyed) {
      this.deployRing.clear();
      this.deployRing.setVisible(false);
      return;
    }
    if (time >= this.deploymentReadyAt) {
      this.deployRing.clear();
      this.deployRing.setVisible(false);
      if (!this.destroyed) this.sprite.setAlpha(this.consumed ? 0.35 : 1);
      return;
    }
    const progress = Phaser.Math.Clamp((time - this.deploymentStartedAt) / Math.max(1, this.deployTimeMs), 0, 1);
    const x = this.x;
    const y = this.y - 46;
    this.deployRing.clear();
    this.deployRing.lineStyle(3, 0xffd36e, 0.78);
    this.deployRing.beginPath();
    this.deployRing.arc(x, y, 15, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress, false);
    this.deployRing.strokePath();
    this.deployRing.lineStyle(1, 0x140b03, 0.82);
    this.deployRing.strokeCircle(x, y, 16);
  }

  markAction(time: number) {
    this.nextAttackAt = time + this.attackRateMs;
    this.sprite.setTint(0xf8efc9);
    this.sprite.scene.time.delayedCall(80, () => {
      if (!this.consumed) this.sprite.clearTint();
    });
    if (this.singleUse) {
      this.consumed = true;
      this.sprite.setAlpha(0.35);
      this.shadow.setAlpha(0.18);
    }
  }

  get upgradeCost() {
    return 55 + this.level * 45;
  }

  upgrade() {
    if (this.destroyed) return [];
    this.level += 1;
    const statCount = Phaser.Math.Between(1, 3);
    const stats = Phaser.Utils.Array.Shuffle(["hp", "damage", "range", "rate"]);
    const changed: string[] = [];
    for (const stat of stats.slice(0, statCount)) {
      if (stat === "hp") {
        const gain = Phaser.Math.Between(25, 70);
        this.maxHp += gain;
        this.hp += gain;
      } else if (stat === "damage") {
        this.damage = Math.round(this.damage + Phaser.Math.Between(3, 12));
      } else if (stat === "range") {
        this.range = Math.round(this.range + Phaser.Math.Between(8, 26));
      } else {
        this.attackRateMs = Math.max(280, Math.round(this.attackRateMs * Phaser.Math.FloatBetween(0.9, 0.97)));
      }
      changed.push(stat);
    }
    this.levelBadge.setText(`${this.level}`).setVisible(true);
    this.syncHpBar();
    return changed;
  }

  setTargetPriority(priority: TowerTargetPriority) {
    this.targetPriority = priority;
  }

  private applyInitialGrowth() {
    const stat = Phaser.Utils.Array.GetRandom(["hp", "damage", "range", "rate"]);
    if (stat === "hp") {
      const gain = Phaser.Math.Between(10, 28);
      this.hp += gain;
      this.maxHp += gain;
    } else if (stat === "damage") {
      this.damage = Math.round(this.damage + Phaser.Math.Between(1, 5));
    } else if (stat === "range") {
      this.range = Math.round(this.range + Phaser.Math.Between(4, 14));
    } else {
      this.attackRateMs = Math.max(320, Math.round(this.attackRateMs * Phaser.Math.FloatBetween(0.94, 0.98)));
    }
  }

  private defaultTargetPriority(id: string): TowerTargetPriority {
    if (id === "mortar_emplacement" || id === "heavy_ballista") return "siege_first";
    if (id === "ice_circle" || id === "rune_obelisk") return "fast_first";
    if (id === "healing_shrine" || id === "command_banner") return "lowest_hp";
    if (id === "powder_trap") return "highest_threat";
    return "nearest";
  }

  takeDamage(amount: number) {
    if (this.destroyed || amount <= 0) return;
    this.hp = Math.max(0, this.hp - amount);
    this.sprite.setTintFill(0xffffff);
    this.sprite.scene.time.delayedCall(70, () => {
      if (!this.destroyed) this.sprite.clearTint();
    });
    this.syncHpBar();
    this.hpBack.setVisible(true);
    this.hpFill.setVisible(true);
    this.levelBadge.setVisible(true);
    if (this.hp <= 0) {
      this.destroyed = true;
      this.sprite.setTint(0x555560).setAlpha(0.38);
      this.shadow.setAlpha(0.12);
      this.deployRing?.clear();
      this.deployRing?.setVisible(false);
      this.hpBack.setVisible(false);
      this.hpFill.setVisible(false);
    }
  }

  heal(amount: number) {
    if (this.destroyed || amount <= 0) return;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.syncHpBar();
    this.hpBack.setVisible(this.hp < this.maxHp);
    this.hpFill.setVisible(this.hp < this.maxHp);
  }

  destroy() {
    this.deployRing?.destroy();
    this.shadow.destroy();
    this.sprite.destroy();
    this.hpBack.destroy();
    this.hpFill.destroy();
    this.levelBadge.destroy();
  }

  private syncHpBar() {
    this.hpFill.width = 40 * Math.max(0, this.hp / this.maxHp);
  }

  private applyDisplaySize(scene: Phaser.Scene, texture: string, id: string) {
    const source = scene.textures.get(texture).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const sourceWidth = source.width || 96;
    const sourceHeight = source.height || 96;
    if (sourceWidth <= 140 && sourceHeight <= 140) return;
    const maxHeight = id === "command_banner" ? 88 : id === "rune_obelisk" ? 84 : 78;
    const maxWidth = id === "mortar_emplacement" ? 96 : 86;
    const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
    this.sprite.setDisplaySize(Math.round(sourceWidth * ratio), Math.round(sourceHeight * ratio));
  }
}
